import React from 'react';
import { FileText, Plus, Edit, Trash2, Copy, Users } from 'lucide-react';
import Button from '../../../components/ui/Button';
import EmptyState from '../../../components/EmptyState';

const OnboardingTemplates = ({ 
  templates, 
  onCreateTemplate, 
  onEditTemplate, 
  onDeleteTemplate 
}) => {
  const handleDuplicateTemplate = (template) => {
    // TODO: Duplicate template logic
    console.log('Duplicate template:', template?.id);
  };

  if (!templates || templates?.length === 0) {
    return (
      <EmptyState
        icon={<FileText />}
        title="No onboarding templates"
        description="Create your first onboarding template to streamline the new hire process. Templates help ensure consistency and completeness."
        primaryAction={{
          label: 'Create Template',
          icon: Plus,
          onClick: onCreateTemplate
        }}
        secondaryAction={{
          label: 'Import Template',
          icon: FileText,
          onClick: () => console.log('Import template')
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Onboarding Templates ({templates?.length})
        </h3>
        <Button onClick={onCreateTemplate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates?.map((template) => (
          <div key={template?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Template Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center mr-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {template?.name || 'Untitled Template'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {template?.tasks?.length || 0} tasks
                  </p>
                </div>
              </div>
              {template?.isDefault && (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  Default
                </span>
              )}
            </div>

            {/* Template Description */}
            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
              {template?.description || 'No description provided.'}
            </p>

            {/* Template Meta */}
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <Users className="h-4 w-4 mr-1" />
              <span>
                {template?.departmentId ? 'Department specific' : 'All departments'}
              </span>
            </div>

            {/* Task Categories */}
            {template?.tasks && template?.tasks?.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {Array.from(new Set(template.tasks.map(task => task?.assigneeType)))?.filter(Boolean)?.slice(0, 3)?.map((assigneeType) => (
                      <span
                        key={assigneeType}
                        className="inline-flex px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-700"
                      >
                        {assigneeType}
                      </span>
                    ))}
                  {template?.tasks?.length > 3 && (
                    <span className="inline-flex px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-700">
                      +{template?.tasks?.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditTemplate(template)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDuplicateTemplate(template)}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Duplicate
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteTemplate(template?.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OnboardingTemplates;